// Libraries
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
// @ts-expect-error library is not typed
import Select, {components as ReactSelectComponents} from 'react-select';

// Supermove
import {Space} from '@supermove/components';
import {useResponsive, useState} from '@supermove/hooks';
import {colors, Typography, INPUT_BORDER_COLOR} from '@supermove/styles';
import {pluralize} from '@supermove/utils';

// Components
import DropdownInput from '../DropdownInput';
import Icon from '../Icon';
import Styled from '../Styled';

const RemoveButton = Styled.ButtonV2`
  padding: 6px;
  align-self: center;
`;

const MultiValueText = Styled.Text`
  ${Typography.Responsive.MicroLabel}
`;

const TruncateText = Styled.Text`
  ${Typography.Responsive.Body}
  color: ${colors.gray.tertiary};
  margin-left: 4px;
`;

const MobileTruncateText = Styled.Text`
  ${Typography.Mobile.Body}
  color: ${colors.gray.tertiary};
  margin-left: 8px;
`;

const controlStyle = ({isMobile}: {isMobile?: boolean}) => ({
  minHeight: isMobile ? 48 : 36,
  paddingTop: 1,
  paddingBottom: 1,
  borderRadius: 3,
  borderWidth: 1,
  borderStyle: 'solid',
  boxShadow: 'none',
  borderColor: INPUT_BORDER_COLOR,
});

const defaultOptionStyle = {
  display: 'block',
  fontSize: 14,
  lineHeight: '19px',
  fontWeight: 300,
};

const placeholderStyle = ({isMobile}: {isMobile?: boolean}) => ({
  fontFamily: 'Avenir',
  fontSize: isMobile ? 16 : 14,
  lineHeight: isMobile ? '22px' : '19px',
  fontWeight: 300,
  color: INPUT_BORDER_COLOR,
});

export interface MultiDropdownInputProps {
  disabled?: boolean;
  required?: boolean;
  isSearchable?: boolean;
  isOptionDisabled?: (option: any) => boolean;
  isLoading?: boolean;
  isPortaled?: boolean;
  hideSelectedOptions?: boolean;
  blurInputOnSelect?: boolean;
  closeMenuOnSelect?: boolean;
  name: string;
  placeholder: string;
  menuPlacement: string;
  value: any[];
  selectedOptions: any[];
  options: any[];
  setFieldValue: (name: string, value: any) => void;
  onChangeValue: (value: any, event: any) => void;
  onSelectValue: (value: any, event: any) => void;
  onDeselectValue: (value: any, event: any) => void;
  onFocus: () => void;
  onInputChange: (value: string) => void;
  components: object;
  style: object;
  optionStyle: object;
  showDescriptionInOption: boolean;
  numberOfVisibleSelections: number;
  isMobile: boolean;
  menuIsOpen: boolean;
  DropdownIndicatorComponent: React.ComponentType;
  onBlur: (event: React.FocusEvent) => void;
}

const MultiDropdownInput = ({
  disabled,
  required,
  isSearchable,
  isOptionDisabled,
  isLoading,
  isPortaled,
  hideSelectedOptions,
  blurInputOnSelect,
  closeMenuOnSelect,
  name,
  placeholder,
  menuPlacement,
  value,
  selectedOptions,
  options,
  setFieldValue,
  onChangeValue,
  onSelectValue,
  onDeselectValue,
  onFocus,
  onInputChange,
  components,
  style,
  optionStyle,
  showDescriptionInOption,
  numberOfVisibleSelections,
  isMobile,
  menuIsOpen,
  DropdownIndicatorComponent,
  onBlur,
}: MultiDropdownInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const responsive = useResponsive();

  return (
    <Select
      isMulti
      maxMenuHeight={500}
      menuIsOpen={menuIsOpen}
      // NOTE(cassie): There's an issue with closeMenuOnSelect=false for certain views,
      // adding blurInputOnSelect=false makes sure the select dropdown isn't closing
      blurInputOnSelect={blurInputOnSelect}
      isDisabled={disabled}
      isOptionDisabled={(option: any) => isOptionDisabled?.(option)}
      isSearchable={isSearchable}
      isLoading={isLoading}
      hideSelectedOptions={hideSelectedOptions}
      closeMenuOnSelect={closeMenuOnSelect}
      name={name}
      placeholder={placeholder}
      menuPlacement={menuPlacement}
      menuPortalTarget={isPortaled && document.body}
      value={selectedOptions || _.map(value, (value) => _.find(options, {value}))}
      options={options}
      formatGroupLabel={DropdownInput.GroupLabel}
      onFocus={() => {
        setIsFocused(true);
        onFocus();
      }}
      onBlur={(event: React.FocusEvent) => {
        onBlur(event);
        setIsFocused(false);
      }}
      onChange={(values: Array<{value: string}>, event: any) => {
        const value = values.map((value) => value.value);
        setFieldValue(name, value);

        // TODO(mark): Hack to avoid the Formik name: onChange.
        onChangeValue(value, event);
        if (event.action === 'select-option') {
          onSelectValue(event.option.value, event);
        }
        if (event.action === 'deselect-option') {
          onDeselectValue(event.option.value, event);
        }
        if (event.action === 'remove-value') {
          onDeselectValue(event.removedValue.value, event);
        }
      }}
      onInputChange={(value: string) => onInputChange(value)}
      components={{
        ClearIndicator: () => null,
        IndicatorSeparator: () => null,
        MultiValueRemove: (props: any) => {
          return (
            <RemoveButton onPress={props.innerProps.onClick}>
              <Icon
                {...props}
                source={Icon.Xmark}
                size={isMobile ? 12 : 10}
                color={props.data.hasError ? colors.red.warning : colors.gray.secondary}
              />
              {isMobile && <Space width={20} />}
            </RemoveButton>
          );
        },
        MultiValue: (props: any) => {
          const selectedValues = props.selectProps.value;

          if (!isFocused && numberOfVisibleSelections) {
            const index = _.findIndex(selectedValues, ['value', props.data.value]);
            if (index === numberOfVisibleSelections) {
              const overCount = selectedValues.length - numberOfVisibleSelections;
              const truncatedText = `+${pluralize('other', overCount, true)}`;
              if (isMobile) {
                return <MobileTruncateText>{truncatedText}</MobileTruncateText>;
              }
              return <TruncateText>{truncatedText}</TruncateText>;
            }

            if (index > numberOfVisibleSelections) {
              return null;
            }
          }

          return (
            <ReactSelectComponents.MultiValue {...props}>
              <MultiValueText responsive={isMobile ? responsive : undefined}>
                {props.data.label}
              </MultiValueText>
            </ReactSelectComponents.MultiValue>
          );
        },
        Group: (props: any) => {
          const allOptions = props.selectProps.options;
          const isFirstGroup = _.get(props, 'data.label') === _.get(allOptions, '0.label');
          const isLastGroup =
            _.get(props, 'data.label') === _.get(allOptions, `${allOptions.length - 1}.label`);

          return (
            <div id={'revertGlobalStyles'}>
              <DropdownInput.GroupContainer>
                {!isFirstGroup && <DropdownInput.GroupDivider />}
                <Space height={12} />
                <ReactSelectComponents.Group {...props} />
                {!isLastGroup && <Space height={4} />}
              </DropdownInput.GroupContainer>
            </div>
          );
        },
        Option: showDescriptionInOption
          ? DropdownInput.OptionWithDescription
          : DropdownInput.Option,
        ...(DropdownIndicatorComponent
          ? {
              DropdownIndicator: (props: any) => {
                return (
                  <ReactSelectComponents.DropdownIndicator {...props}>
                    <DropdownIndicatorComponent />
                  </ReactSelectComponents.DropdownIndicator>
                );
              },
            }
          : {
              DropdownIndicator: (props: any) => {
                return (
                  <MultiDropdownInput.DropdownIndicator {...props}>
                    <Icon
                      source={Icon.ChevronDown}
                      size={14}
                      color={colors.gray.secondary}
                      style={{marginLeft: 4, marginRight: 4, marginTop: 2}}
                    />
                  </MultiDropdownInput.DropdownIndicator>
                );
              },
            }),
        ...components,
      }}
      styles={{
        container: (current: object, state: {isDisabled?: boolean}) => ({
          ...current,
          pointerEvents: 'auto',
          cursor: state.isDisabled ? 'not-allowed' : 'text',
        }),
        control: (current: object, state: {isFocused?: boolean; isDisabled?: boolean}) => ({
          ...current,
          '&:hover': {
            borderColor: disabled
              ? INPUT_BORDER_COLOR
              : state.isFocused
                ? colors.blue.interactive
                : colors.blue.hover,
          },
          ...controlStyle({isMobile}),
          ...style,
          ...(state.isFocused ? {borderColor: colors.blue.interactive} : {}),
          'pointerEvents': state.isDisabled ? 'none' : 'auto',
          'cursor': disabled ? 'default' : 'text',

          // Height should always be null to allow the area to expand.
          'height': null,

          // Custom 'required' feature to change the background-color.
          'backgroundColor': DropdownInput.getBackgroundColor({
            disabled,
            required,
          }),
        }),
        placeholder: (current: any) => ({
          ...current,
          ...placeholderStyle({isMobile}),
        }),
        option: (current: any) => ({
          ...current,
          ...defaultOptionStyle,
          ...optionStyle,
        }),
        menu: (current: any) => ({
          ...current,
          display: 'block',
        }),
        menuList: (current: any) => ({
          ...current,
          display: 'block',
          paddingTop: 4,
          paddingBottom: 4,
        }),
        multiValue: (current: any, state: any) => ({
          ...current,
          marginRight: isMobile ? '8px' : '4px',
          backgroundColor: state.data.hasError ? colors.red.accent : colors.gray.border,
          height: 24,
          alignItems: 'center',
        }),
        input: (current: any) => ({
          ...current,
          input: {
            textAlign: 'left',
            fontFamily: 'Avenir',
            fontWeight: 500,
          },
        }),
      }}
    />
  );
};

MultiDropdownInput.Option = ({optionComponentProps = {}, OptionComponent, ...props}: any = {}) => {
  const {
    children,
    className,
    cx,
    getStyles,
    isDisabled,
    isFocused,
    isSelected,
    innerRef,
    innerProps,
    data,
  } = props;
  return (
    <div
      ref={innerRef}
      css={getStyles('option', props)} // eslint-disable-line
      className={cx(
        {
          'option': true,
          'option--is-disabled': isDisabled,
          'option--is-focused': isFocused,
          'option--is-selected': isSelected,
        },
        className,
      )}
      {...innerProps}
    >
      <OptionComponent option={data} disabled={isDisabled} {...optionComponentProps}>
        {children}
      </OptionComponent>
    </div>
  );
};

// --------------------------------------------------
// Props
// --------------------------------------------------
MultiDropdownInput.propTypes = {
  value: PropTypes.array,
  numberOfVisibleSelections: PropTypes.number,
  isSearchable: PropTypes.bool,
  isPortaled: PropTypes.bool,
  blurInputOnSelect: PropTypes.bool,
  isMobile: PropTypes.bool,
  onInputChange: PropTypes.func,
  setFieldValue: PropTypes.func,
  isOptionDisabled: PropTypes.func,
  onChangeValue: PropTypes.func,
  onSelectValue: PropTypes.func,
  onDeselectValue: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  components: PropTypes.object,
  optionStyle: PropTypes.object,
};

MultiDropdownInput.defaultProps = {
  value: [],
  numberOfVisibleSelections: null,
  isSearchable: true,
  isPortaled: false,
  blurInputOnSelect: false,
  isMobile: false,
  onInputChange: () => {},
  setFieldValue: () => {},
  isOptionDisabled: () => {},
  onChangeValue: () => {},
  onSelectValue: () => {},
  onDeselectValue: () => {},
  onFocus: () => {},
  onBlur: () => {},
  components: {},
  optionStyle: {},
};

MultiDropdownInput.DropdownIndicator = ReactSelectComponents.DropdownIndicator;

export default MultiDropdownInput;
